import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'

import Header from '@/components/Header'
import FullPageLoader from '@/components/FullPageLoader'
import ChatFaqFormModal from '@/components/ChatFaqFormModal'
import { userGrantsSelector } from '@/store/slices/user'
import {
  deleteChatFaq,
  getChatFaqs,
  publishChatFaq,
  saveChatFaq,
  setChatFaqSuggestion,
  testChatFaq,
  unpublishChatFaq,
  updateChatFaq
} from '@/store/slices/chat-faq'
import { toFormattedDate } from '@/utils/common'
import { CHAT_FAQ_SCOPES, getChatFaqScopeLabel } from '@/constant/chat-faq'

const ChatFaqPage = () => {
  const dispatch = useDispatch()
  const grants = useSelector(userGrantsSelector)

  const [faqs, setFaqs] = useState([])
  const [editingFaq, setEditingFaq] = useState()
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [testQuestion, setTestQuestion] = useState('')
  const [testContext, setTestContext] = useState('')
  const [testResult, setTestResult] = useState()
  const [isTesting, setIsTesting] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [policyFilter, setPolicyFilter] = useState('ALL')
  const [suggestionFilter, setSuggestionFilter] = useState('ALL')

  const hasAccess = action => grants.chat_faq?.includes(action)

  const fetchFaqs = async () => {
    try {
      setIsLoading(true)
      const { data } = await dispatch(getChatFaqs()).unwrap()
      setFaqs(data)
    } catch (error) {
      toast.error('Failed to load FAQs')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFaqs()
  }, [])

  const openCreateForm = () => {
    setEditingFaq(undefined)
    setIsFormVisible(true)
  }

  const openEditForm = faq => {
    setEditingFaq(faq)
    setIsFormVisible(true)
  }

  const saveFaq = async body => {
    try {
      setIsSaving(true)
      if (editingFaq) {
        await dispatch(updateChatFaq({ id: editingFaq.id, body })).unwrap()
      } else {
        await dispatch(saveChatFaq(body)).unwrap()
      }
      setIsFormVisible(false)
      toast.success(editingFaq ? 'FAQ updated' : 'FAQ draft created')
      await fetchFaqs()
    } catch (error) {
      const message = error.data?.error === 'GEMINI_NOT_CONFIGURED'
        ? 'Configure GEMINI_API_KEY before editing a published FAQ'
        : error.data?.error === 'CHAT_FAQ_SUGGESTION_LIMIT_REACHED'
          ? 'A maximum of four storefront suggestions is allowed'
          : 'Failed to save FAQ'
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const togglePublication = async faq => {
    try {
      setIsLoading(true)
      if (faq.status === 'PUBLISHED') {
        await dispatch(unpublishChatFaq(faq.id)).unwrap()
        toast.success('FAQ moved to draft')
      } else {
        await dispatch(publishChatFaq(faq.id)).unwrap()
        toast.success('FAQ published with a new embedding')
      }
      await fetchFaqs()
    } catch (error) {
      const message = error.data?.error === 'GEMINI_NOT_CONFIGURED'
        ? 'Configure GEMINI_API_KEY before publishing'
        : 'Failed to change FAQ status'
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const removeFaq = async faq => {
    if (!window.confirm(`Delete FAQ “${faq.question}”?`)) {
      return
    }

    try {
      setIsLoading(true)
      await dispatch(deleteChatFaq(faq.id)).unwrap()
      toast.success('FAQ deleted')
      await fetchFaqs()
    } catch (error) {
      toast.error('Failed to delete FAQ')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleSuggestion = async faq => {
    try {
      setIsLoading(true)
      await dispatch(setChatFaqSuggestion({
        id: faq.id,
        recommendedForChat: !faq.recommendedForChat
      })).unwrap()
      toast.success(faq.recommendedForChat
        ? 'Storefront suggestion removed'
        : 'FAQ set as a storefront suggestion')
      await fetchFaqs()
    } catch (error) {
      toast.error('Failed to update storefront suggestion')
    } finally {
      setIsLoading(false)
    }
  }

  const visibleFaqs = faqs.filter(faq => {
    const searchText = `${faq.question} ${faq.answer}`.toLocaleLowerCase()
    const matchesKeyword = searchText.includes(keyword.trim().toLocaleLowerCase())
    const matchesStatus = statusFilter === 'ALL' || faq.status === statusFilter
    const matchesPolicy = policyFilter === 'ALL' || (faq.scope || 'GENERAL') === policyFilter
    const matchesSuggestion = suggestionFilter === 'ALL' ||
      (suggestionFilter === 'SUGGESTION' ? faq.recommendedForChat === true : faq.recommendedForChat !== true)
    return matchesKeyword && matchesStatus && matchesPolicy && matchesSuggestion
  })

  const testAnswer = async event => {
    event.preventDefault()
    if (!testQuestion.trim()) {
      return
    }

    try {
      setIsTesting(true)
      setTestResult(undefined)
      const recentConversation = testContext.split('\n').map(text => text.trim()).filter(Boolean)
        .slice(-2).map(text => ({ sender: 'CUSTOMER', text: text.slice(0, 500) }))
      const { data } = await dispatch(testChatFaq({ question: testQuestion.trim(), recentConversation })).unwrap()
      setTestResult(data)
    } catch (error) {
      const message = error.data?.error === 'GEMINI_NOT_CONFIGURED'
        ? 'Configure GEMINI_API_KEY to test Gemini answers'
        : 'Gemini answer test failed'
      toast.error(message)
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <Header title="Chat FAQ">
      {isLoading && <FullPageLoader />}

      {
        isFormVisible && (
          <ChatFaqFormModal
            faq={editingFaq}
            recommendedCount={faqs.filter(faq => faq.status === 'PUBLISHED' && faq.recommendedForChat).length}
            isSaving={isSaving}
            onClose={() => setIsFormVisible(false)}
            onSubmit={saveFaq}
          />
        )
      }

      <section className="border border-gray-200 rounded-lg p-5 mb-8 bg-gray-50">
        <div className="font-bold text-lg">Test grounded answer</div>
        <p className="text-sm text-gray-600 mt-1 mb-3">
          Test published FAQ answers and follow-up questions using the same retrieval rules as the storefront.
        </p>
        <form className="flex flex-col gap-3" onSubmit={testAnswer}>
          <label className="text-sm text-gray-700 flex flex-col gap-1">
            Previous customer questions (optional, one per line; latest two used)
            <textarea
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="Jadwal pre-order Figur Uji Chat A"
              value={testContext}
              onChange={event => setTestContext(event.target.value)}
              rows={2}
              maxLength={1001}
            />
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg"
            aria-label="Customer question"
            placeholder="Ask a customer question"
            value={testQuestion}
            onChange={event => setTestQuestion(event.target.value)}
            maxLength={1000}
          />
          <button
            type="submit"
            className="px-6 py-2 rounded-full bg-gray-800 text-white disabled:opacity-50"
            disabled={isTesting || !testQuestion.trim()}
          >
            {isTesting ? 'Testing...' : 'Test answer'}
          </button>
          </div>
        </form>

        {
          testResult && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
              <div className="font-semibold">
                {testResult.resolved ? 'Resolved' : testResult.clarification ? 'Needs clarification' : 'Unresolved'}
              </div>
              <div className="mt-1 whitespace-pre-wrap">
                {testResult.answer || (testResult.diagnostics?.outcome?.startsWith('PROVIDER_')
                  ? 'The provider could not complete the answer. Check the diagnostic outcome below.'
                  : 'No published FAQ supplied enough evidence. This question would be escalated.')}
              </div>
              {
                !!testResult.matches?.length && (
                  <div className="mt-3 text-sm text-gray-600">
                    Matches: {testResult.matches.map(match => `${match.question} (${match.similarity.toFixed(3)})`).join(', ')}
                  </div>
                )
              }
              {testResult.diagnostics && (
                <div className="mt-3 text-sm text-gray-600 break-words">
                  <div>Outcome: {testResult.diagnostics.outcome} · {testResult.diagnostics.durationMs} ms</div>
                  <div>Retrieval: {testResult.diagnostics.retrieval?.mode || 'Unavailable'}</div>
                  {testResult.diagnostics.timings?.map((timing, index) => (
                    <div key={`${timing.stage}-${index}`}>
                      {timing.stage}: {timing.durationMs} ms / {timing.timeoutMs} ms · {timing.outcome}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }
      </section>

      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-bold text-lg">Managed FAQs</div>
          <div className="text-sm text-gray-500">Only published entries are available to Gemini.</div>
        </div>
        {
          hasAccess('create') && (
            <button
              type="button"
              className="px-6 py-2 rounded-full border border-gray-800 bg-gray-800 text-white"
              onClick={openCreateForm}
            >
              Create FAQ
            </button>
          )
        }
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <label className="flex flex-col gap-y-1 text-sm font-semibold text-gray-700">
          Keyword
          <input
            type="search"
            className="p-2 border border-gray-300 rounded-lg font-normal"
            placeholder="Search questions and answers"
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-y-1 text-sm font-semibold text-gray-700">
          Status
          <select
            className="p-2 border border-gray-300 rounded-lg font-normal"
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
          </select>
        </label>
        <label className="flex flex-col gap-y-1 text-sm font-semibold text-gray-700">
          Policy
          <select
            className="p-2 border border-gray-300 rounded-lg font-normal"
            value={policyFilter}
            onChange={event => setPolicyFilter(event.target.value)}
          >
            <option value="ALL">All policies</option>
            {CHAT_FAQ_SCOPES.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-y-1 text-sm font-semibold text-gray-700">
          Storefront suggestion
          <select
            className="p-2 border border-gray-300 rounded-lg font-normal"
            value={suggestionFilter}
            onChange={event => setSuggestionFilter(event.target.value)}
          >
            <option value="ALL">All FAQs</option>
            <option value="SUGGESTION">Suggestions</option>
            <option value="NOT_SUGGESTION">Not suggestions</option>
          </select>
        </label>
      </div>

      {
        !isLoading && !faqs.length && (
          <div className="text-center text-gray-500 py-10 border border-gray-200 rounded-lg">
            No FAQs yet. Create a draft to begin.
          </div>
        )
      }

      <div className="flex flex-col gap-y-4">
        {
          visibleFaqs.map(faq => (
            <article key={faq.id} className="border border-gray-200 rounded-lg p-5">
              <div className="flex justify-between gap-x-6">
                <div className="flex-1">
                  <div className="flex items-center gap-x-3">
                    <div className="font-bold text-lg">{faq.question}</div>
                    <span className={faq.status === 'PUBLISHED'
                      ? 'px-3 py-1 rounded-full text-xs bg-green-100 text-green-700'
                      : 'px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700'}>
                      {faq.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700">
                      {getChatFaqScopeLabel(faq.scope)}
                    </span>
                    {
                      faq.status === 'PUBLISHED' && faq.recommendedForChat && (
                        <span className="px-3 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                          Storefront suggestion
                        </span>
                      )
                    }
                  </div>
                  <div className="mt-2 whitespace-pre-wrap text-gray-700">{faq.answer}</div>
                  <div className="mt-3 text-xs text-gray-500">
                    Last updated {faq.lastModifiedAt ? toFormattedDate(faq.lastModifiedAt) : '-'} by {faq.lastModifiedBy || '-'}
                  </div>
                </div>

                <div className="flex flex-wrap items-start justify-end gap-2">
                  {
                    hasAccess('update') && (
                      <>
                        <button
                          type="button"
                          className={faq.recommendedForChat
                            ? 'px-4 py-1.5 rounded-full border border-yellow-600 text-yellow-800'
                            : 'px-4 py-1.5 rounded-full border border-gray-700'}
                          onClick={() => toggleSuggestion(faq)}
                          disabled={isLoading}
                        >
                          {faq.recommendedForChat ? 'Remove suggestion' : 'Set as suggestion'}
                        </button>
                        <button
                          type="button"
                          className="px-4 py-1.5 rounded-full border border-gray-700"
                          onClick={() => openEditForm(faq)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="px-4 py-1.5 rounded-full border border-gray-700 bg-gray-700 text-white"
                          onClick={() => togglePublication(faq)}
                        >
                          {faq.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                        </button>
                      </>
                    )
                  }
                  {
                    hasAccess('delete') && (
                      <button
                        type="button"
                        className="px-4 py-1.5 rounded-full border border-red-500 text-red-500"
                        onClick={() => removeFaq(faq)}
                      >
                        Delete
                      </button>
                    )
                  }
                </div>
              </div>
            </article>
          ))
        }
        {!isLoading && faqs.length > 0 && visibleFaqs.length === 0 && (
          <div className="text-center text-gray-500 py-10 border border-gray-200 rounded-lg">
            No FAQs match these filters.
          </div>
        )}
      </div>
    </Header>
  )
}

export default ChatFaqPage
